import React, { useState, useMemo } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';

export interface Column<T> {
  title: string;
  dataIndex: keyof T;
  sorter?: boolean;
  render?: (value: T[keyof T], record: T) => React.ReactNode;
  width?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  dataSource: T[];
  rowKey: keyof T;
  loading?: boolean;
  pageSize?: number;
  onRowClick?: (record: T) => void;
}

type SortOrder = 'asc' | 'desc' | null;

function DataTable<T extends object>({
  columns,
  dataSource,
  rowKey,
  loading = false,
  pageSize = 10,
  onRowClick,
}: DataTableProps<T>) {
  const { theme, isMobile } = useTheme();
  const [sortColumn, setSortColumn] = useState<keyof T | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = (column: keyof T) => {
    if (sortColumn === column) {
      if (sortOrder === 'asc') {
        setSortOrder('desc');
      } else if (sortOrder === 'desc') {
        setSortColumn(null);
        setSortOrder(null);
      } else {
        setSortOrder('asc');
      }
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = [...dataSource];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item =>
        columns.some(col => {
          const value = item[col.dataIndex];
          return String(value).toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    // Sort
    if (sortColumn && sortOrder) {
      filtered.sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        let comparison = 0;
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          comparison = aVal.localeCompare(bVal);
        } else if (typeof aVal === 'number' && typeof bVal === 'number') {
          comparison = aVal - bVal;
        } else {
          comparison = String(aVal).localeCompare(String(bVal));
        }

        return sortOrder === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [dataSource, sortColumn, sortOrder, searchTerm, columns]);

  // Paginate the filtered and sorted data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedData.slice(startIndex, endIndex);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);

  // Reset to page 1 when search term changes
  useMemo(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const styles: Record<string, React.CSSProperties> = {
    container: {
      background: theme.card,
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    searchContainer: {
      padding: '16px',
      borderBottom: `1px solid ${theme.border}`,
    },
    searchInput: {
      width: '100%',
      maxWidth: '300px',
      padding: '10px 14px',
      fontSize: '14px',
      border: `1px solid ${theme.inputBorder}`,
      borderRadius: '6px',
      background: theme.inputBg,
      color: theme.text,
      outline: 'none',
      transition: 'border-color 0.2s',
    },
    tableWrapper: {
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: isMobile ? '600px' : 'auto',
    },
    th: {
      padding: '14px 16px',
      textAlign: 'left' as const,
      fontWeight: 600,
      fontSize: '13px',
      color: theme.textSecondary,
      background: theme.tableHeader,
      borderBottom: `1px solid ${theme.border}`,
      cursor: 'pointer',
      userSelect: 'none' as const,
      whiteSpace: 'nowrap' as const,
      transition: 'background 0.2s',
    },
    thContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    sortIcon: {
      fontSize: '10px',
      opacity: 0.5,
    },
    sortIconActive: {
      opacity: 1,
      color: '#2563eb',
    },
    td: {
      padding: '14px 16px',
      fontSize: '14px',
      color: theme.text,
      borderBottom: `1px solid ${theme.border}`,
      whiteSpace: 'nowrap' as const,
    },
    tr: {
      transition: 'background 0.2s',
      cursor: onRowClick ? 'pointer' : 'default',
    },
    loading: {
      padding: '40px',
      textAlign: 'center' as const,
      color: theme.textSecondary,
    },
    empty: {
      padding: '40px',
      textAlign: 'center' as const,
      color: theme.textSecondary,
    },
    pagination: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px',
      borderTop: `1px solid ${theme.border}`,
      flexWrap: 'wrap' as const,
      gap: '12px',
    },
    paginationInfo: {
      fontSize: '14px',
      color: theme.textSecondary,
    },
    paginationButtons: {
      display: 'flex',
      gap: '8px',
    },
    pageButton: {
      padding: '8px 14px',
      fontSize: '13px',
      fontWeight: 500,
      border: `1px solid ${theme.inputBorder}`,
      borderRadius: '6px',
      background: theme.inputBg,
      color: theme.text,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    pageButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
          Loading data...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search players..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={String(col.dataIndex)}
                  style={styles.th}
                  onClick={() => col.sorter && handleSort(col.dataIndex)}
                >
                  <div style={styles.thContent}>
                    {col.title}
                    {col.sorter && (
                      <span
                        style={{
                          ...styles.sortIcon,
                          ...(sortColumn === col.dataIndex ? styles.sortIconActive : {}),
                        }}
                      >
                        {sortColumn === col.dataIndex
                          ? sortOrder === 'asc'
                            ? '▲'
                            : sortOrder === 'desc'
                              ? '▼'
                              : '⇅'
                          : '⇅'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={styles.empty}>
                  {searchTerm ? 'No players found matching your search' : 'No data available'}
                </td>
              </tr>
            ) : (
              paginatedData.map((record, index) => (
                <tr
                  key={String(record[rowKey]) || index}
                  style={styles.tr}
                  onClick={() => onRowClick?.(record)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = theme.tableRowHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {columns.map((col) => (
                    <td key={String(col.dataIndex)} style={{ ...styles.td, width: col.width }}>
                      {col.render
                        ? col.render(record[col.dataIndex], record)
                        : String(record[col.dataIndex] ?? '')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredAndSortedData.length > pageSize && (
        <div style={styles.pagination}>
          <span style={styles.paginationInfo}>
            Showing {Math.min((currentPage - 1) * pageSize + 1, filteredAndSortedData.length)} -{' '}
            {Math.min(currentPage * pageSize, filteredAndSortedData.length)} of {filteredAndSortedData.length}
            {searchTerm && ` (filtered from ${dataSource.length} total)`}
          </span>
          <div style={styles.paginationButtons}>
            <button
              style={{
                ...styles.pageButton,
                ...(currentPage === 1 ? styles.pageButtonDisabled : {}),
              }}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span style={{ padding: '8px 14px', fontSize: '13px', color: theme.text }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              style={{
                ...styles.pageButton,
                ...(currentPage >= totalPages ? styles.pageButtonDisabled : {}),
              }}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;